import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'

import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import SettingsIcon from '@mui/icons-material/Settings'
import ShareIcon from '@mui/icons-material/Share'
import VideocamIcon from '@mui/icons-material/Videocam'
import HomeIcon from '@mui/icons-material/Home'
import BusinessIcon from '@mui/icons-material/Business'
import RateReviewIcon from '@mui/icons-material/RateReview'

import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'

import { getEnv } from '@/utils/helpers'

export type MeetingLinkData = {
  id: string
  name: string
  slug: string
  specialities: string[]
  staffCount: number
  meetingPlaceTypes: AvailabilityMeetingPlace[]
  pendingOrdersCount: number
}

export type MeetingLinksCardViewProps = {
  data: MeetingLinkData[]
  loading?: boolean
  onShare?: (meetingLink: { id: string; name: string; slug: string }) => void
}

const meetingPlaceIcons: Record<AvailabilityMeetingPlace, { icon: React.ReactNode; label: string }> = {
  [AvailabilityMeetingPlace.VISIO_CONFERENCE]: { icon: <VideocamIcon fontSize="small" />, label: 'Video' },
  [AvailabilityMeetingPlace.AT_HOME]: { icon: <HomeIcon fontSize="small" />, label: 'Home' },
  [AvailabilityMeetingPlace.CENTER]: { icon: <BusinessIcon fontSize="small" />, label: 'Center' },
  [AvailabilityMeetingPlace.SECOND_OPINION]: { icon: <RateReviewIcon fontSize="small" />, label: 'Review' },
}

const MeetingLinksCardView = (props: MeetingLinksCardViewProps) => {
  const { data, loading, onShare } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const handleCopyLink = (slug: string) => {
    const patientUrl = `${getEnv('VITE_MEDCONNECT_PATIENT_URL')}/meeting-links/${slug}/booking`
    navigator.clipboard.writeText(patientUrl)
    enqueueSnackbar(t('page.meetingLinks.card.linkCopied'), { variant: 'success' })
  }

  const handlePreview = (slug: string) => {
    const patientUrl = `${getEnv('VITE_MEDCONNECT_PATIENT_URL')}/meeting-links/${slug}`
    window.open(patientUrl, '_blank')
  }

  return (
    <Grid container spacing={2}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {data.map((row) => (
        <Grid item xs={12} sm={6} md={4} key={row.id}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1 }}>
              <Stack spacing={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {row.name}
                </Typography>

                {/* Meeting place type icons */}
                {row.meetingPlaceTypes.length > 0 && (
                  <Stack direction="row" spacing={0.5}>
                    {row.meetingPlaceTypes.map((place) => (
                      <Tooltip key={place} title={meetingPlaceIcons[place]?.label || place}>
                        <Box sx={{ color: 'text.secondary' }}>
                          {meetingPlaceIcons[place]?.icon}
                        </Box>
                      </Tooltip>
                    ))}
                  </Stack>
                )}

                {/* Specialities */}
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {row.specialities.map((speciality) => (
                    <Chip key={speciality} label={speciality} size="small" variant="outlined" />
                  ))}
                </Stack>

                {/* Stats row */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {t('page.meetingLinks.card.staffCount', { count: row.staffCount })}
                  </Typography>
                  {row.pendingOrdersCount > 0 && (
                    <Badge badgeContent={row.pendingOrdersCount} color="error" max={99}>
                      <Typography variant="caption" color="text.secondary">
                        {t('page.meetingLinks.card.pendingOrders')}
                      </Typography>
                    </Badge>
                  )}
                </Stack>
              </Stack>
            </CardContent>

            {/* Action row */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, pb: 1.5 }}>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={t('page.meetingLinks.card.copyLink')}>
                  <IconButton size="small" onClick={() => handleCopyLink(row.slug)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('page.meetingLinks.card.preview')}>
                  <IconButton size="small" onClick={() => handlePreview(row.slug)}>
                    <LaunchRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('page.meetingLinks.card.share')}>
                  <IconButton size="small" onClick={() => onShare?.({ id: row.id, name: row.name, slug: row.slug })}>
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Tooltip title={t('page.meetingLinks.card.settings')}>
                <IconButton size="small" onClick={() => navigate(`/meeting-links/${row.id}`)}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

export default MeetingLinksCardView
